
import { css } from "lit";

export const defaultGlobalStyles = css`
    html {
        /* MIDAS definitions - see https://midas.maersk-digital.net/visual-design-language/ */
        --neutrals-grey-01: #141414;
        --neutrals-grey-01-rgb: 20, 20, 20;
        --neutrals-grey-02: #353535;
        --neutrals-grey-03: #535353;
        --neutrals-grey-04: #878787;
        --neutrals-grey-04-rgb: 135, 135, 135;
        --neutrals-grey-05: #B2B2B2;
        --neutrals-grey-06: #CFCFCF;
        --neutrals-grey-06-rgb: 207, 207, 207;
        --neutrals-grey-07: #DBDBDB;
        --neutrals-grey-08: #EEEEEE;
        --neutrals-grey-09: #F8F8F8;
        --neutrals-grey-10: #FFFFFF;
        --neutrals-grey-10-rgb: 255, 255, 255;

        --primary-blue-01: #00233D;
        --primary-blue-02: #003D6D;
        --primary-blue-04: #0072AA;
        --primary-blue-05: #058EC6;
        --primary-blue-05-rgb: 5, 142, 198;
        --primary-blue-07: #42AFD6;
        --primary-blue-10: #B5E0F4;

        --warning-yellow-7: #FFB302;
        --warning-yellow-8: #FFC319;
        --warning-yellow-10: #FFDE74;
        --warning-yellow-10-rgb: 255, 222, 116;
        
        --negative-red-6: #F9381C;
        --negative-red-10: #FFEDED;

        --heading-6-size: 16px;
        --heading-6-height: 24px;
        --heading-6-weight: bold;

        --paragraph-size: 14px;
        --paragraph-height: 20px;
        --paragraph-weight: normal;

        /* Voyage Specific */
        --voyage-error-red: #FF0000;
        --voyage-error-red-rgb: 255, 0, 0;
        --voyage-good-green: #27AE60;
        --voyage-good-green-rgb: 39, 174, 96;
        --voyage-blue-dark: #0073AB;
        --voyage-alarm-red: #B80012;
        --voyage-black: #000000;

        --route1-color: #1D7DEA;
        --route1-transparent-bg-color: #009E5912;
        --route2-color: #1DC6BC;
        --route2-transparent-bg-color: #006CB612;
        --route3-color: #D61E92;
        --route3-transparent-bg-color: #A91ADB12;
        --route4-color: #611ECF;

        --route4-transparent-bg-color: #A91ADB12;
        --route5-color: #F77F00;
        --route5-transparent-bg-color: #A91ADB12;
        --route5-color: #F77F00;

        --current-route: #63CEFC;
        --requested-route: #27AE60;
        --approved-plan-color: var(--voyage-good-green);

        --timeline-current-time-color: var(--voyage-good-green);
        --timeline-bg-color: var(--neutrals-grey-02);
        --timeline-tick-color: var(--neutrals-grey-09);
        --timeline-fg-color: var(--neutrals-grey-10);
        --timeline-button-selected-color: var(--neutrals-grey-07);
        --timeline-divider-color: var(--neutrals-grey-03);

        --tooltip-text-color: var(--neutrals-grey-10);

        --voy-alert-message-background-color: #FFEDED;

        --voyage-parametric-roll-color: #FE92E9;
        --voyage-heavy-rolling-color: #8454D6;
        --voyage-transverse-acceleration-color: #BF6410;

        --voyage-info-warn-background-color: #C03030;

        --voyage-performance-chart-simulated-color: #59A9CE;

        --waypoint-fill-color: #FFFFFF80;

        --voyage-passed-line-color: #A3A3A3;

        --waypoint-editor-invalid-coordinate: #E21D44;
        --waypoint-editor-new-coordinate: #1D8BE2;
        --waypoint-editor-remove-button-color: #FF0000;
        --waypoint-editor-revert-button: #828282;
        --waypoint-editor-coordinates-text: #000000;
        --waypoint-editor-delete-button-color: #FF0000;

        --performance-viewer-actual-color: #FF0000;
        --performance-viewer-actual-no-signal-color: rgb(224, 169, 174);
        --performance-viewer-simulated-color: #5ACFFF;
        --performance-viewer-threshold-color: #F456D5;
        --performance-viewer-graph-border-color: rgba(0, 0, 0, 0.3);
        --performance-viewer-nextleg-color: #FF9A1E;
        --performance-viewer-range-color: #000000;
        --performance-viewer-dial-background: #FFFFFF;
        --performance-viewer-graph-background: rgba(53,53,53,0.5);
        --performance-viewer-graph-background-planning: rgba(0, 0, 0, 0.5);
        --performance-viewer-dial-text-color: #FFFFFF;
        
        --chart-shore-color: #CEC67B;
        --chart-intertidal-color: #8BBE93;
        --chart-very-shallow-color: #83C2E7;
        --chart-medium-shallow-color: #A6D4E8;
        --chart-medium-water-color: #C8E3D5;
        --chart-deep-water-color: #DFF7E7;

        --tide-data-color: #058EC6;

        --component-heading-ff: MaerskHeadline;
        --component-heading-size: var(--heading-6-size, 20px);
        --component-heading-height: var(--heading-6-height, 20px);
        --component-heading-weight: var(--heading-6-weight, normal);

        --component-paragraph-ff: MaerskText;
        --component-paragraph-size: var(--paragraph-size, 12px);
        --component-paragraph-height: var(--paragraph-height, 12px);
        --component-paragraph-weight: var(--paragraph-weight, normal);

        --enp-layers-chapterBorderColor: #000000;
        --enp-layers-fill-color: rgba(0, 0, 0, 0.07);
        --enp-layers-bookBorderColor: #FF00FF;

        --nav-areas-layers-color: #000000;
        --nav-areas-layers-fill-color: rgba(0, 0, 0, 0.07);

        --whale-zones-layers-border-color: #000000;
        --whale-zones-layers-fill-color: rgba(0, 0, 0, 0.07);
        --whale-zones-layers-restricted-color: #FF0000;
        --whale-zones-layers-avoid-color: #FFC319;

        --marpol-layer-border-color: #000000;
        --marpol-layer-fill-color: rgba(0, 0, 0, 0.07);

        --eca-seca-layer-border-color: #000000;
        --eca-seca-layer-fill-color: rgba(0, 0, 0, 0.07);

        --security-layer-border-color: #000000;
        --security-layer-fill-color: rgba(0, 0, 0, 0.07);

        --precautionary-zones-layer-border-color: #B80012;
        --precautionary-zones-layer-fill-color: rgba(184, 0, 18, 0.15);

        --bwm-layer-border-color: #000000;
        --bwm-layer-fill-color: rgba(0, 0, 0, 0.07);

        --restricted-areas-layer-border-color: #B80012;
        --restricted-areas-layer-corridor-color: #00008B;
        --restricted-areas-layer-fill-color: rgba(184, 0, 18, 0.15);

        --schedule-edit-omit-port: #FF0000;
        --schedule-edit-warning-header: #828282;
        --schedule-edit-warning-background: rgba(255, 222, 116, 0.63);
        --schedule-edit-warning-background-border: #FFC319;

        --voyage-box-shadow: rgba(0, 0, 0, 0.1);
        --secondary-voyage-box-shadow: rgba(0, 0, 0, 0.25);
        --border-color-1: #C4C4C4;

        --voyage-past-trail-popup-background: rgba(53, 53, 53, 0.70);
        --voyage-past-trail-marker: #545454;
        --voyage-past-trail-line: #545454;
        --voyage-past-trail-highlighted-marker:  #7e7e7e;

        --pilot-point: #962681;

        --non-maersk-vessel-icon-color: #FFD029;
        --vessel-highlight-color: #FF031C;
        --position-warning-marker-warn: #DEB307;
        --position-warning-marker-error: #D82206;

        --scroll-bar-color: #C4C4C4;

        /* Maersk Design System */
        --primary-blue-300: #42B0D5;
        --primary-blue-700: #005487;
        --primary-blue-900: #00243D;
        --primary-blue-300: #42B0D5;

        --functional-grey-100: #F7F7F7;
        --functional-grey-200: #EDEDED;
        --functional-grey-400: #CFCFCF;
        --functional-grey-600: #878787;
        --functional-grey-700: #545454;
        --functional-grey-800: #353535;
        --functional-grey-900: #141414;

        --feedback-sucess-light: #C5E5C2;

        --feedback-danger-light: #E9B2B7;
        --feedback-danger: #B80012;

        --gray-input-background: #EDE9E9
    }
`;
